<template>
  <div
    class="v-tags-creator"
    :class="{
      [version]: true,
      [extraClass]: !!extraClass,
      ['is-incorrect']: _displayError,
      ['is-correct']: !currentIncorrect && currentInputValue
    }"
  >
    <label for="vTagsCreator">{{ label }}</label>
    <div class="input-wrapper">
      <input
        id="vTagsCreator"
        type="text"
        :name="name"
        :value="currentInputValue"
        :placeholder="placeholder"
        @keyup.enter="_onInput"
        @input="_onInput"
        @paste="_onPaste"
        @focus="isFocused = true"
        @blur="isFocused = false"
        :ref="refName"
      />
      <div class="service-message">
        <div v-if="_displayError">
          <span>{{ errorMessage }}</span>
        </div>
        <div v-if="!currentIncorrect && currentInputValue">
          <span>Press enter to add email</span>
        </div>
      </div>
    </div>

    <div class="tags-list">
      <VTag
        v-for="(tag, idx) in value"
        :key="idx"
        :_key="idx"
        :label="tag"
        :removeHandler="(key, label) => _remove(label)"
        :preventEllipsis="preventEllipsis"
      />
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";

import VTag from "@/components/VTag";

export default Vue.component("VTagsCreator", {
  props: {
    label: VueTypes.string.def(""),
    name: VueTypes.string.isRequired,
    splitSymbol: VueTypes.string,
    placeholder: VueTypes.string.def("Placeholder"),
    valueValidationFunc: VueTypes.func,
    ignoreNonValid: VueTypes.bool.def(false),
    value: VueTypes.array.def([]),
    extraClass: VueTypes.string.def(""),
    version: VueTypes.oneOf(["old", "new"]).def("old"),

    // preventing tags being ellipsed with dots
    preventEllipsis: VueTypes.bool.def(false),
    onRemove: VueTypes.func,
    errorString: VueTypes.string.def("Email is incorrect"),
    minValuesCount: VueTypes.number.def(0)
  },

  components: {
    VTag
  },

  data() {
    return {
      errorMessage: this.errorString || "",
      submitted: false,
      currentInputValue: "",
      currentIncorrect: false,
      tagsList: [...this.value],
      isFocused: false,
      refName: "tagsInput"
    };
  },

  watch: {
    currentInputValue: function() {
      const validationResponse =
        this.valueValidationFunc &&
        this.valueValidationFunc(this.currentInputValue);
      this.currentIncorrect = !this._isValidationHandlerResponsePositive(
        validationResponse
      );
    }
  },

  methods: {
    /**
     * Check if validation function could be called
     * @returns {boolean}
     * @private
     */
    _isValidationFuncCallable() {
      return (
        this.valueValidationFunc &&
        typeof this.valueValidationFunc === "function"
      );
    },

    _blurInput() {
      this.$refs[this.refName].blur();
    },

    _remove(tag) {
      if (this.tagsList.length - 1 < this.minValuesCount) {
        return
      }

      this.tagsList = this.tagsList.filter(t => t !== this.tagsList[tag]);
      this.$emit("change", this.tagsList);
      this.onRemove(this.tagsList);
    },

    _onInput(e) {
      const {
        target: { value },
        inputType,
        data,
        keyCode
      } = e;

      if (inputType === "insertFromPaste") {
        return;
      }

      this.errorMessage = "";

      if (this.currentIncorrect) {
        this.currentIncorrect = false;
      }

      if (this.submitted) {
        this.submitted = false;
      }

      // checking split symbol reached or hit "enter"
      if (data === this.splitSymbol || keyCode === 13) {
        if (this.tagsList.includes(this.currentInputValue)) {
          this.currentIncorrect = true;
          this.errorMessage = "Email already exists";
          this._blurInput();
          this.submitted = true;
        } else {
          if (this._isValidationFuncCallable()) {
            const validationResponse = this.valueValidationFunc(
              this.currentInputValue
            );
            if (this._isValidationHandlerResponsePositive(validationResponse)) {
              this.tagsList.push(this.currentInputValue);
              this.currentInputValue = "";
              this.currentIncorrect = false;
              this._blurInput();
            } else {
              this.currentIncorrect = true;

              if (typeof validationResponse === "string") {
                this.errorMessage = validationResponse;
              }

              this.submitted = true;
            }
          } else {
            this.tagsList.push(this.currentInputValue);
            this.currentInputValue = "";
            this._blurInput();
          }
        }

        this.$emit("change", this.tagsList);
      } else {
        if (this._isValidationFuncCallable()) {
          const validationResponse = this.valueValidationFunc(value);

          if (typeof validationResponse === "string") {
            this.errorMessage = validationResponse;
          }

          this.currentInputValue = value;
        }
      }
    },

    _onPaste(e) {
      const clipboardData = e.clipboardData;

      // get text from paste
      const pastedData = clipboardData.getData("Text");

      if (pastedData && pastedData.includes(this.splitSymbol)) {
        const pastedArray = pastedData.split(this.splitSymbol);

        if (!this.valueValidationFunc) {
          this.tagsList = [...new Set([...this.tagsList, ...pastedArray])];
          this.currentInputValue = "";
        } else {
          const notValid = [];
          const validatedArray = pastedArray.filter(item => {
            const validationResponse = this.valueValidationFunc(item.trim());

            if (this._isValidationHandlerResponsePositive(validationResponse)) {
              return item;
            } else {
              notValid.push(item);
            }
          });

          this.tagsList = [...new Set([...this.tagsList, ...validatedArray])];
          if (this.ignoreNonValid) {
            this.currentInputValue = "";
          } else {
            this.currentInputValue = notValid.join(this.splitSymbol);
          }
        }

        e.preventDefault();
        e.stopPropagation();
      } else {
        this.currentInputValue = pastedData;
      }

      this.$emit("change", this.tagsList);
    },

    /**
     * Check if response from handler function is valid
     * Returns true if valid otherwise false
     * @param response
     * @returns {boolean}
     * @private
     */
    _isValidationHandlerResponsePositive(response) {
      return response !== false && typeof response !== "string";
    }
  },

  computed: {
    _displayError() {
      return this.submitted && this.currentIncorrect;
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-tags-creator";

/*---OLD style---*/

.#{$ns}.old {
  max-width: 800px;

  .input-wrapper {
    > div {
      font-size: 10px;
      margin-top: 7px;
      line-height: 1.2em;
      padding-left: 2px;
    }
  }

  input {
    border: none;
    outline: none;
    width: 100%;
    font-size: 14px;

    &::placeholder {
      font-family: "Lato-Light", sans-serif;
      font-style: italic;
      color: #4e4e4e;
    }
  }

  label {
    display: block;
    margin-bottom: 18px;
    color: #131e2c;
  }

  &__tags-list {
    display: flex;
    flex-wrap: wrap;
    margin-top: 20px;
    max-height: 200px;
    overflow: auto;
  }

  &.is-incorrect {
    .input-wrapper {
      div,
      input {
        color: $danger-color;
      }
    }

    input {
      width: 65%;
    }
  }
}

/*---NEW style---*/
.#{$ns}.new {
  width: 100%;
  height: auto;
  margin-top: 0;

  .input-wrapper {
    width: 100%;
    font-size: 10px;
    margin-top: 7px;
    line-height: 1.2em;

    .service-message {
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-size: 14px;
      line-height: 1.2;
      font-family: "Lato-Light", sans-serif;
      text-align: left;
      vertical-align: center;
      height: 1.2em;
    }
  }

  input {
    border: 1px solid #c5c5c5;
    border-radius: 3px;
    outline: none;
    width: 100%;
    font-size: 15px;
    font-family: "Lato-Regular", sans-serif;
    padding: 8px;
    margin-bottom: 5px;

    &::placeholder {
      font-family: "Lato-Light", sans-serif;
      font-style: italic;
      color: #4e4e4e;
    }
  }

  label {
    display: block;
    text-align: left;
    margin: 0 0 14px 8px;
    color: #131e2c;
    font-family: "Lato-Bold", Arial, sans-serif;
  }

  .tags-list {
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    margin: 5px 0 20px 0;
    height: 253px;
    overflow: auto;
    border: 1px solid #c5c5c5;
    border-radius: 3px;
    padding-left: 5px;
    padding-top: 5px;
  }

  &.is-correct {
    color: $primary-color;

    input {
      border: 1px solid $primary-color;
    }
  }

  &.is-incorrect {
    color: $input-error-color;

    input {
      border: 1px solid $input-error-color;
    }
  }
}
</style>
